"use client";

import { useCallback } from "react";
import Icon from "@/components/library/Icon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAlert } from "@/hooks/useAlert";

interface CopyRowProps {
  /** Omitted where the surrounding block already heads the row. */
  label?: string;
  value: string;
  /** Already-translated so this stays free of translation keys. */
  copiedMessage: string;
  failedMessage: string;
  copyLabel: string;
  testId?: string;
}

/** Readonly value plus a copy button — the row shape the Start node and JotForm share. */
export default function CopyRow({
  label,
  value,
  copiedMessage,
  failedMessage,
  copyLabel,
  testId,
}: Readonly<CopyRowProps>) {
  const { setAlert } = useAlert();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setAlert({ status: "success", message: copiedMessage, secondsToClose: 4 });
    } catch {
      setAlert({ status: "error", message: failedMessage, secondsToClose: 4 });
    }
  }, [value, copiedMessage, failedMessage, setAlert]);

  return (
    <div className="space-y-1" data-testid={testId}>
      {label && <Label className="text-xs">{label}</Label>}
      <div className="flex gap-2">
        <Input
          type="text"
          value={value}
          readOnly
          aria-label={label}
          className="flex-1 text-xs font-mono bg-100"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCopy}
          aria-label={copyLabel}
          className="shrink-0"
        >
          <Icon icon="copy" />
        </Button>
      </div>
    </div>
  );
}
